import React, { useEffect, useMemo, useState } from 'react';
import { finalize } from 'rxjs/operators';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import { SimType } from 'utils/api/payloads/microApi';
import CustomSIMInput from 'components/CustomInput/CustomSIMInput';
import Button from 'components/CustomButtons/Button';
import styles from 'assets/jss/material-dashboard-pro-react/views/settingsSubpageStyle';
import { makeStyles } from '@material-ui/core';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { CardType } from '../index';
import ChargeBanner from '../../ChargeBanner';
import { useDispatch, useSelector } from 'react-redux';
import { getSimMgmtLabels } from 'redux/modules/dynamicLabels/selectors';
import ChangeNumberWrapper from 'views/Auth/common/ChangeNumberWrapper';
import OTP from 'components/OTP/OTP';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { UsersService } from 'services/users/users';
import { OperationContextEnum } from 'utils/api/payloads/omantelApi';
import { setToast } from 'redux/modules/toast/actions';
import OtpTab from 'views/Numbers/Details/Settings/subcomponents/ResetAdslPasswordDialog/subcomponents/OtpTab';

// @ts-ignore
const useStyles = makeStyles(styles);
interface IReplaceSimDialogProps {
	isOpen: boolean;
	hideDialog: () => void;
	simNumber: string;
	onChange: (value: string) => void;
	errorMessage?: string | null;
	setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
	addReplaceSimCard: () => void;
	simNumberValid: boolean;
	setSimNumberValid: React.Dispatch<React.SetStateAction<boolean>>;
	buttonText: string;
	simType: SimType;
	loading: boolean;
	cardType: CardType | undefined;
	msisdn: string;
	otp: string;
	setOtp: React.Dispatch<React.SetStateAction<string>>;
	isIncorrectOtp: boolean;
	setIsInCorrectOtp: React.Dispatch<React.SetStateAction<boolean>>;
	isSIMError: boolean;
}

const steps = {
	CHANGE: 0,
	OTP: 1,
};

const ReplaceSimDialog = ({
	isOpen,
	hideDialog,
	simNumber,
	onChange,
	errorMessage,
	setErrorMessage,
	addReplaceSimCard,
	simNumberValid,
	setSimNumberValid,
	buttonText,
	simType,
	loading,
	cardType,
	msisdn,
	otp,
	setOtp,
	isIncorrectOtp,
	setIsInCorrectOtp,
	isSIMError,
}: IReplaceSimDialogProps) => {
	const classes = useStyles();
	const simMgmtLabels = useSelector(getSimMgmtLabels);
	const [isOTPStep, setOTPStep] = useState(false);

	const [activeTab, setActiveTab] = useState(steps.CHANGE);

	const dispatch = useDispatch();

	const requestForOtp = () => {
		dispatch(showLoadingIndicator());
		setIsInCorrectOtp(false);
		setOtp('');
		const operationContext =
			simType === SimType.DATA_SIM
				? OperationContextEnum.NEWDATASIM
				: cardType === CardType.SIM
				? OperationContextEnum.NEWSAWAPHYSICALSIM
				: OperationContextEnum.NEWSAWAESIM;
		UsersService.requestForOtp(operationContext, msisdn)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(() => {
				setOTPStep(true);

				setToast({
					error: false,
					label: translate('otp.token.sent'),
				});
			});
	};

	useEffect(() => {
		if (isSIMError) {
			setActiveTab(steps.CHANGE);
		}
		if (errorMessage) {
			dispatch(
				setToast({
					error: true,
					label: errorMessage,
					actionType: translate('error'),
				})
			);
		}
	}, [isSIMError, errorMessage]);

	const nextStepAction = () => {
		setActiveTab((activeTab) => {
			switch (activeTab) {
				case steps.CHANGE:
					requestForOtp();
					return activeTab + 1;
				case steps.OTP:
					return activeTab;
				default:
					return activeTab;
			}
		});
	};

	const cancelAction = () => {
		switch (activeTab) {
			case steps.CHANGE:
				return hideDialog();
			case steps.OTP:
				setIsInCorrectOtp(false);
				if (cardType === CardType.ESIM) {
					setErrorMessage('');
				}
				return setActiveTab(activeTab - 1);
			default:
				return hideDialog();
		}
	};

	const nextStepDisabled = useMemo(() => {
		switch (activeTab) {
			case steps.CHANGE:
				return !simNumberValid;
			case steps.OTP:
				return otp === '';
			default:
				return false;
		}
	}, [activeTab, simNumberValid, otp]);

	const hideNewSimDialog = () => {
		setActiveTab(steps.CHANGE);
		setIsInCorrectOtp(false);
		setErrorMessage('');
		hideDialog();
	};

	const headerText =
		simType === SimType.DATA_SIM
			? translate(
					cardType === CardType.SIM
						? 'numbers.settings.replace.data.sim.card'
						: 'numbers.settings.replace.data.esim.card'
			  )
			: translate(
					cardType === CardType.SIM
						? 'numbers.settings.replace.sawa.sim.card'
						: 'numbers.settings.replace.sawa.esim.card'
			  );

	return (
		<CustomDialog
			isOpen={isOpen}
			hideDialog={hideNewSimDialog}
			width={500}
			header={headerText}
			dialogBody={
				<ChangeNumberWrapper
					tabs={[
						{
							name: headerText,
							title: headerText,
							content: (
								<ConditionalRender
									show={loading}
									onTrue={() => <CustomCircularProgress text={''} transparent loading />}
									onFalse={() => (
										<>
											{cardType === CardType.SIM && (
												<div className={classes.inputWrapper}>
													<CustomSIMInput
														labelText={translate('numbers.settings.new.sim.number')}
														formControlProps={{
															fullWidth: true,
														}}
														showError={!!errorMessage}
														onErrorChange={setSimNumberValid}
														helperText={errorMessage || ''}
														supportAsyncValidation={true}
														inputProps={{
															value: simNumber,
															onChange: onChange,
														}}
													/>
												</div>
											)}
											{/* show charge disclaimer if is present */}
											<ConditionalRender
												show={!!simMgmtLabels.chargeBanner && simType !== SimType.SAWA_SIM}
												onTrue={() => <ChargeBanner value={simMgmtLabels.chargeBanner} />}
											/>
										</>
									)}
								/>
							),
						},
						{
							name: translate('change.number.verification'),
							title: translate('change.number.verification'),
							content: (
								<ConditionalRender
									show={isOTPStep}
									onTrue={() => (
										<div className={classes.otpWrapper}>
											<OtpTab
												otp={otp}
												setOtp={setOtp}
												sendOtp={requestForOtp}
												otpIncorrect={isIncorrectOtp}
												inOneInput={false}
												submitOTP={addReplaceSimCard}
											/>
										</div>
									)}
									onFalse={() => (
										<div>
											<CustomCircularProgress text={translate('loading')} loading={true} />
										</div>
									)}
								/>
							),
						},
					]}
					activeTab={activeTab}
					setActiveTab={setActiveTab}
					buttons={[
						{
							text: activeTab === steps.CHANGE ? translate('cancel') : translate('back'),
							onClick: cancelAction,
							color: 'tertiary-light' as types.theme.buttonColor,
						},
						{
							text: activeTab === steps.OTP ? buttonText : translate('next'),
							onClick: activeTab !== steps.OTP ? nextStepAction : addReplaceSimCard,
							color: 'primary-light' as types.theme.buttonColor,
							disabled:
								cardType === CardType.ESIM && activeTab === steps.CHANGE ? false : nextStepDisabled,
							id: 'next-button',
						},
					]}
				/>
			}
		/>
	);
};

export default ReplaceSimDialog;
